// The dashboard filter bar's state and how it travels (item 4, design decisions 2b, 2c, 5).
//
// A bar is seven strings:
//   branch, region, salesExec, prodType - '' not set (the widget's stored setting stands),
//                                         '-1' All, '0' the dashboard user's own setting,
//                                         or comma-separated ids;
//   period                              - a date_modifier_func, e.g. 'last30days';
//   dateFrom, dateTo                    - a custom range as yyyy/MM/dd.
// A period and a custom range are never held together: the backend 400s on both.
//
// The same bar lives in the route query (so a reload, Back and a shared link restore it) and
// in Vuex (so the menu, which opens the dashboard with no query, restores it too).

export const DIMENSIONS = ['branch', 'region', 'salesExec', 'prodType']

// Written to the query when the user deliberately chose widget settings, so that choice is
// not mistaken for "nothing chosen yet" and replaced by the saved view.
export const WIDGET_SETTINGS_KEY = 'view'
export const WIDGET_SETTINGS_VALUE = 'widgets'

// The list/graph widget endpoints that accept the bar's four dimensions (not dates yet).
// TASKS (getUserTasks) has no dimensions, so the bar leaves it alone.
const BAR_ENDPOINTS = ['getUserGraphData', 'getTop10Customers', 'getTop10Quotes', 'getLatestQuotes']

const DIMENSION_VALUE = /^(-1|0|[1-9]\d*(,[1-9]\d*)*)$/
const PERIOD_VALUE = /^[A-Za-z][A-Za-z0-9]*$/
const DATE_VALUE = /^\d{4}[/-]\d{2}[/-]\d{2}$/

export function emptyBar() {
  return { branch: '', region: '', salesExec: '', prodType: '', period: '', dateFrom: '', dateTo: '' }
}

function text(value) {
  if (Array.isArray(value)) value = value[0]
  return value === null || value === undefined ? '' : String(value).trim()
}

// Anything that does not look like the backend's vocabulary is dropped, so a hand-edited or
// stale link opens the dashboard on widget settings for that dimension rather than on a 400.
function clean(values) {
  const bar = emptyBar()
  DIMENSIONS.forEach(key => {
    const value = text(values[key]).replace(/\s+/g, '')
    bar[key] = DIMENSION_VALUE.test(value) ? value : ''
  })
  const period = text(values.period)
  const from = text(values.dateFrom)
  const to = text(values.dateTo)
  // A custom range, when one is present, wins over a period - the user picked it last.
  bar.dateFrom = DATE_VALUE.test(from) ? from.replace(/-/g, '/') : ''
  bar.dateTo = DATE_VALUE.test(to) ? to.replace(/-/g, '/') : ''
  if (!bar.dateFrom && !bar.dateTo && PERIOD_VALUE.test(period) && period !== 'custom') {
    bar.period = period
  }
  return bar
}

export function barFromQuery(query) {
  return clean(query || {})
}

// True when the query says anything about the bar - including an explicit "widget settings".
export function queryHasBar(query) {
  const q = query || {}
  return [...DIMENSIONS, 'period', 'dateFrom', 'dateTo', WIDGET_SETTINGS_KEY].some(key => text(q[key]) !== '')
}

export function hasDate(bar) {
  return !!(bar && (bar.period || bar.dateFrom || bar.dateTo))
}

export function isCustomRange(bar) {
  return !!(bar && (bar.dateFrom || bar.dateTo))
}

export function isEmptyBar(bar) {
  return !bar || (DIMENSIONS.every(key => !bar[key]) && !hasDate(bar))
}

export function sameBar(a, b) {
  const x = clean(a || {})
  const y = clean(b || {})
  return Object.keys(x).every(key => x[key] === y[key])
}

// Parameters for v1/getUserWidgetData: only what is set; a custom range wins over a period.
export function barToParams(bar) {
  const b = clean(bar || {})
  const params = barDimensionParams(b)
  if (b.dateFrom || b.dateTo) {
    if (b.dateFrom) params.dateFrom = b.dateFrom
    if (b.dateTo) params.dateTo = b.dateTo
  } else if (b.period) {
    params.period = b.period
  }
  return params
}

// Parameters for the list/graph endpoints: the four dimensions, no dates.
export function barDimensionParams(bar) {
  const b = clean(bar || {})
  const params = {}
  DIMENSIONS.forEach(key => {
    if (b[key]) params[key] = b[key]
  })
  return params
}

export function barToQuery(bar) {
  const params = barToParams(bar)
  return Object.keys(params).length ? params : { [WIDGET_SETTINGS_KEY]: WIDGET_SETTINGS_VALUE }
}

// The route query with the bar's keys replaced, keeping anything else (e.g. ?toast=).
export function mergeBarIntoQuery(query, bar) {
  const next = {}
  Object.keys(query || {}).forEach(key => {
    if (![...DIMENSIONS, 'period', 'dateFrom', 'dateTo', WIDGET_SETTINGS_KEY].includes(key)) next[key] = query[key]
  })
  return Object.assign(next, barToQuery(bar))
}

// PUT v1/dashboardSavedView: the four dimensions and a named period. Never dates - a saved
// view keeps a named period only, and the backend refuses dateFrom/dateTo.
export function savedViewBody(bar) {
  const b = clean(bar || {})
  return { branch: b.branch, region: b.region, salesExec: b.salesExec, prodType: b.prodType, period: b.period }
}

export function barFromSavedView(view) {
  if (!view || !view.saved) return null
  return clean({ branch: view.branch, region: view.region, salesExec: view.salesExec, prodType: view.prodType, period: view.period })
}

// --- who may change what (W9 §2.2) ---------------------------------------------------------
// The same rule the reports screen applies (Reports.vue:945-960): a permission held at level 5
// is "Restricted", and a restricted user only ever sees their own branch. On the dashboard it
// is worked out per user, not per widget - a dashboard is one screen and one Branch control.
export const RESTRICTED_LEVEL = 5

// The permission codes behind a dashboard: the `permissionCode` of every report the user's own
// widgets point at. `reportId` comes from what the dashboard already fetches (the widget data
// and v1/getUserWidgetDrills) and `permissionCode` is already on `reports/getAllReports`
// (Reports.vue:598 reads it), so this needs nothing new from the backend.
export function widgetPermissionCodes(widgetRows, reports) {
  const byId = {}
  ;(reports || []).forEach(report => {
    if (report && report.id !== null && report.id !== undefined) byId[String(report.id)] = report
  })

  const codes = []
  ;(widgetRows || []).forEach(row => {
    const id = row && row.reportId !== null && row.reportId !== undefined ? String(row.reportId) : null
    const report = id ? byId[id] : null
    const code = report && report.permissionCode ? String(report.permissionCode) : null
    if (code && codes.indexOf(code) < 0) codes.push(code)
  })
  return codes
}

// null when this user may see the whole company; otherwise their own branch, which the Branch
// control is then fixed to. A restricted user with no defBranch is still restricted - the panel
// says so rather than silently showing everything, as `noBranchHandle` does on the reports
// screen.
export function branchRestriction({ permissions, codes, profile } = {}) {
  const restricted = (permissions || []).some(
    held =>
      held &&
      held.permission &&
      (codes || []).indexOf(held.permission.permissionCode) >= 0 &&
      held.permissionLevel == RESTRICTED_LEVEL // eslint-disable-line eqeqeq
  )
  if (!restricted) return null

  const branch = (profile && profile.defBranch) || null
  if (!branch || branch.id === null || branch.id === undefined) {
    return { restricted: true, branch: null, branchId: '' }
  }
  return { restricted: true, branch, branchId: String(branch.id) }
}

// The bar as a restricted user is allowed to have it: their own branch, whatever the query,
// Vuex or their saved view asked for. Everything else is left alone.
export function applyRestriction(bar, restriction) {
  if (!restriction || !restriction.restricted || !restriction.branchId) return bar
  if (bar && bar.branch === restriction.branchId) return bar
  return Object.assign(emptyBar(), bar, { branch: restriction.branchId })
}

export function withQuery(path, params) {
  const keys = Object.keys(params || {})
  if (!keys.length) return path
  const qs = keys.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`).join('&')
  return path + (path.indexOf('?') >= 0 ? '&' : '?') + qs
}

export function barAppliesTo(endpoint) {
  return !!endpoint && BAR_ENDPOINTS.some(name => endpoint.indexOf(name) >= 0)
}

// Region 5 is inactive and unnamed yet carries the largest active pipeline (decision 2c), so it
// gets a label rather than a blank entry.
export function regionLabel(region) {
  const name = region && region.regionName ? String(region.regionName).trim() : ''
  return name || `Region ${region ? region.id : ''} (unnamed)`
}

// yyyy/MM/dd <-> Date, local time (the dashboard's dates are UK calendar days).
export function formatBarDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return ''
  const pad = n => (n < 10 ? `0${n}` : `${n}`)
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`
}

export function parseBarDate(value) {
  if (!value || !DATE_VALUE.test(value)) return null
  const [y, m, d] = value.split(/[/-]/).map(Number)
  return new Date(y, m - 1, d)
}

// '' | '-1' | '0' | '3,5' <-> the MultiSelect's array of option values.
export function dimensionToSelection(value) {
  return value ? String(value).split(',') : []
}

// A special choice (All, own setting) stands alone; picking an id drops it, and picking a
// special choice drops the ids. Widget settings is the empty selection.
export function selectionToDimension(previous, next) {
  const prev = previous || []
  const added = (next || []).filter(v => !prev.includes(v))
  const special = v => v === '-1' || v === '0' || v === ''
  const addedSpecial = added.find(special)
  if (addedSpecial !== undefined) return addedSpecial
  return (next || []).filter(v => !special(v)).join(',')
}
